import type { FC } from 'react';
import { useMemo } from 'react';

import { Plus, RMpp } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  useDeleteDevice,
  useDevices,
  useHasSubscription,
} from 'src/api/queries';
import {
  Button,
  CardContent,
  Modal,
  NotificationBox,
  Sheet,
  SomethingWentWrong,
  Spinner,
} from 'src/components';
import { Link2 } from 'src/components/Link2';
import { URLS } from 'src/utils/urls/urls';

import { useAuth0 } from '@auth0/auth0-react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import { useMaxDevicesError } from '../utils/useMaxDevicesError';
import { filterActivePaperTabletClients } from '../utils/utils';
import { DeviceTable } from './DeviceTable';
import { OneTimeCode } from './OneTimeCode';
import { VerifyMultiplePairModal } from './VerifyMultiplePairModal';

const PairingText: FC<{
  hasSubscription: boolean;
  activeDevices: number;
}> = (props) => {
  const auth = useAuth0();
  const maxDeviceCount = deviceHelpers.getMaxDeviceCount(auth.user);
  const pairingLimitReached = props.activeDevices >= maxDeviceCount;

  if (pairingLimitReached) {
    return (
      <div data-cy="pairing-limit-reached" className="w-full lm:w-2/3">
        <Typography variant="body-md">
          <Trans>
            You&apos;ve reached the limit of paired devices. Unpair one of your
            paper tablets before pairing a new one.
          </Trans>
        </Typography>
      </div>
    );
  }

  return (
    <div className="w-full lm:w-2/3">
      <Typography variant="body-md">
        <Trans>
          Pair your account with up to {maxDeviceCount} paper tablets. Once
          paired, you can access notes on mobile and desktop apps.
        </Trans>
      </Typography>
    </div>
  );
};

const routeApi = getRouteApi('/_auth/_layout/device');

export const Remarkable = () => {
  const { t } = useLingui();

  const { showOtp } = routeApi.useSearch();
  const connectedDevices = useDevices();
  const auth = useAuth0();

  const deleteDeviceMutation = useDeleteDevice();
  const [hasMaxDevicesError, setMaxDevicesError] = useMaxDevicesError();

  const hasSubscription = useHasSubscription();

  const activeDevices = useMemo(
    () => filterActivePaperTabletClients(connectedDevices.data),
    [connectedDevices.data]
  );

  const hasActiveDevice = activeDevices.length > 0;
  const maxDeviceCount = deviceHelpers.getMaxDeviceCount(auth.user);
  const canConnectMoreDevices = activeDevices.length < maxDeviceCount;

  if (
    connectedDevices.isPending ||
    deleteDeviceMutation.isPending ||
    hasSubscription.isPending
  ) {
    return <Spinner />;
  }

  if (
    connectedDevices.isError ||
    deleteDeviceMutation.isError ||
    !!hasSubscription.error
  ) {
    return <SomethingWentWrong error={hasSubscription.error || null} />;
  }
  if (showOtp) {
    return (
      <Sheet>
        <CardContent
          dataCy="pair-device-otp-content"
          className="flex flex-col gap-16"
        >
          <OneTimeCode
            successRedirectUrl={URLS.DEVICE_REMARKABLE}
            description={t`Enter this one-time code on your paper tablet, and tap enter.`}
            allowPairing={!canConnectMoreDevices}
            hideCopyToClipboard // Cannot copy from browser to tablet
            componentLocation={ComponentLocations.DEVICE.DEVICE}
          />
        </CardContent>
      </Sheet>
    );
  }

  return (
    <div className="flex flex-col gap-16 lm:gap-24">
      {hasMaxDevicesError && (
        <NotificationBox
          onClose={() => {
            setMaxDevicesError(false);
          }}
          variant="warning"
          title={t`Maximum devices already paired`}
          data-cy="max-devices-paired"
        >
          <Trans>Unpair existing devices in order to pair new ones.</Trans>
        </NotificationBox>
      )}
      <Sheet>
        <Modal.Root>
          <VerifyMultiplePairModal />
          <CardContent className="my-12 flex flex-row items-center justify-between">
            <div className="flex flex-row items-center">
              <RMpp size={32} className="mr-16" />
              <Typography variant="heading-subtle-2xl" as="h2">
                <Plural
                  value={activeDevices.length}
                  one="Paper tablet"
                  other="Paper tablets"
                />
              </Typography>
            </div>
          </CardContent>

          <CardContent borderTop className="flex flex-col space-y-20 pb-0">
            <div className="flex flex-col justify-between gap-20 text-left lm:mt-8 lm:flex-row">
              <PairingText
                hasSubscription={hasSubscription.data}
                activeDevices={activeDevices.length}
              />

              <div>
                {hasActiveDevice ? (
                  <Modal.Trigger asChild>
                    <Button
                      data-cy="pair-device-button"
                      variant="tertiary"
                      className="w-full lm:w-auto"
                      disabled={!canConnectMoreDevices}
                      size="medium"
                    >
                      <Plus size={24} />
                      <span>
                        <Trans>Pair device</Trans>
                      </span>
                    </Button>
                  </Modal.Trigger>
                ) : (
                  <Link2
                    data-cy="pair-device-button"
                    search={{ showOtp: true }}
                    size="medium"
                    as="button"
                    variant="primary"
                    componentLocation={ComponentLocations.DEVICE.DEVICE}
                  >
                    <Plus size={24} />
                    <span>
                      <Trans>Pair device</Trans>
                    </span>
                  </Link2>
                )}
              </div>
            </div>

            {!hasSubscription.data && activeDevices.length > 1 && (
              <NotificationBox
                title={<Trans>Cloud limit reached</Trans>}
                variant="info"
                data-cy="multiple-devices-cloud-warning"
              >
                <Typography variant="body-md" className="mb-0">
                  <Trans>
                    Without Connect, documents stop syncing after 50 days of
                    inactivity. Reactivate cloud storage and sync documents
                    across all devices by subscribing.
                  </Trans>
                  <Button
                    as="a"
                    to={URLS.SUBSCRIPTION}
                    variant="tertiary"
                    className="mt-12"
                    size="small"
                  >
                    <Trans>Subscribe</Trans>
                  </Button>
                </Typography>
              </NotificationBox>
            )}
          </CardContent>
        </Modal.Root>
      </Sheet>
      <DeviceTable deviceType={'paper-tablet'} />
    </div>
  );
};
